import { ArrowLeft, Bell, ChevronDown, HelpCircle, PanelLeft, Search, Shield } from "lucide-react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { canPerform } from "../auth/actions"
import { isPlatformUser } from "../auth/platform-roles"
import { useAuth } from "../contexts/AuthContext"
import { enterAdmin } from "../libs/admin-entry"
import { adminNavigationConfig } from "../libs/admin-navigation"
import { isCustomerPath } from "../libs/customer-paths"
import { getActiveNavTitle, getNavigationConfig } from "../libs/navigation"
import { cn } from "../libs/utils"
import { CustomerSwitcher } from "./CustomerSwitcher"
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar"
import { Button } from "./ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"

interface TopBarProps {
  onMenuClick?: () => void
  onCommandPalette?: () => void
  className?: string
}

export function TopBar({ onMenuClick, onCommandPalette, className }: TopBarProps) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { customerId } = useParams<{ customerId: string }>()
  // Match /admin as a path segment so sibling routes aren't misclassified.
  const isAdminView = location.pathname === "/admin" || location.pathname.startsWith("/admin/")
  // Show the admin entry to any platform user. Mirrors the route's
  // RequirePlatformUser gate so the menu item never dead-ends.
  const canUseAdmin = isPlatformUser(user?.platformRole)
  const canUseCustomerApp = canPerform(user?.platformRole, "app.customers")
  const showFleetAppAction = isAdminView ? canUseCustomerApp : canUseAdmin

  // Page title shown in the top bar, derived from the active nav item.
  const navItems = isAdminView
    ? adminNavigationConfig
    : customerId && isCustomerPath(location.pathname)
      ? getNavigationConfig(customerId)
      : []
  const pageTitle = getActiveNavTitle(location.pathname, navItems)
  const roleLabel = user?.isGlobalAdmin ? "Administrator" : "Fleet Manager"
  const searchPlaceholder = isAdminView ? "Search admin…" : "Search vehicles, chargers, sites…"

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }

  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      console.error("Logout failed:", error)
    }
  }

  return (
    <header
      className={cn(
        "sticky top-0 z-30 flex items-center gap-[18px] border-b border-hairline bg-[color-mix(in_srgb,var(--bg)_82%,transparent)] px-7 py-3.5 backdrop-blur-[12px]",
        className
      )}
    >
      {/* Mobile nav trigger — sidebar panel icon, hidden at desktop breakpoint.
          btn-unstyled exempts it from the global button touch-target rule, whose
          forced `display:inline-flex` (specificity 0,1,1) would otherwise beat
          Tailwind's `desktop:hidden` and leak this inert button onto desktop. */}
      {onMenuClick && (
        <Button
          variant="ghost"
          size="icon"
          className="btn-unstyled desktop:hidden -ml-2"
          onClick={onMenuClick}
          aria-label="Open navigation"
        >
          <PanelLeft className="size-5" />
        </Button>
      )}

      {/* ADMIN pill (admin console only) */}
      {isAdminView && (
        <span className="admin-console-badge hidden flex-none items-center gap-[7px] rounded-[8px] border px-[11px] py-1.5 sm:inline-flex">
          <Shield className="size-[14px]" strokeWidth={2} style={{ color: "var(--admin)" }} />
          <span
            className="text-[11px] font-bold tracking-[0.1em]"
            style={{ color: "var(--admin)" }}
          >
            ADMIN
          </span>
        </span>
      )}

      {/* Breadcrumb (customer switcher / area) + page title */}
      <div className="min-w-0">
        {isAdminView ? (
          <div className="text-[11.5px] tracking-[0.04em] text-content-2">Administration</div>
        ) : (
          <CustomerSwitcher variant="crumb" />
        )}
        <div className="truncate font-display text-[18px] font-semibold leading-[1.1] tracking-[-0.01em] text-content">
          {pageTitle}
        </div>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Search → command palette (⌘K) where wired */}
      <div className="relative hidden w-[280px] max-w-[32vw] sm:block">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-content-3"
          strokeWidth={1.8}
          aria-hidden
        />
        <button
          type="button"
          onClick={() => onCommandPalette?.()}
          aria-label="Search"
          className="btn-unstyled flex h-10 w-full items-center truncate rounded-control border border-hairline bg-surface pl-9 pr-3.5 text-left text-[13.5px] text-content-3 transition-colors hover:bg-surface-2"
        >
          {searchPlaceholder}
        </button>
      </div>

      {/* Notifications */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Notifications"
            className="btn-unstyled relative flex size-10 flex-none items-center justify-center rounded-control border border-hairline bg-surface text-content-2 transition-colors hover:bg-surface-2 hover:text-content"
          >
            <Bell className="size-[18px]" strokeWidth={1.8} />
            <span
              className="absolute right-2 top-2 size-[7px] rounded-full bg-danger ring-2 ring-surface"
              aria-label="1 new notification"
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80">
          <DropdownMenuLabel>Notifications</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <div className="p-4 text-center text-sm text-content-2">No new notifications</div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Help → in-app documentation, which lives in the admin console: only for
          users who can open it — a customer never lands in the admin portal. */}
      {(isAdminView || canUseAdmin) && (
        <button
          type="button"
          aria-label="Help and documentation"
          onClick={() => (isAdminView ? navigate("/admin/docs") : enterAdmin("/admin/docs"))}
          className="btn-unstyled hidden size-10 flex-none items-center justify-center rounded-control border border-hairline bg-surface text-content-2 transition-colors hover:bg-surface-2 hover:text-content sm:flex"
        >
          <HelpCircle className="size-[18px]" strokeWidth={1.8} />
        </button>
      )}

      {/* User menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="User menu"
            className="btn-unstyled flex flex-none items-center gap-[9px] rounded-control border border-hairline bg-surface py-[5px] pl-[5px] pr-2.5 text-content transition-colors hover:bg-surface-2"
          >
            <Avatar className="size-[30px] rounded-[8px]">
              <AvatarImage src={user?.avatar} alt={user?.name} />
              <AvatarFallback
                className="rounded-[8px] font-display text-[12.5px] font-semibold text-on-accent"
                style={{ background: "linear-gradient(135deg, var(--green), var(--accent))" }}
              >
                {user ? getInitials(user.name) : "?"}
              </AvatarFallback>
            </Avatar>
            <div className="hidden text-left leading-[1.15] desktop:block">
              <div className="text-[12.5px] font-semibold text-content">{user?.name || "User"}</div>
              <div className="text-[11px] text-content-3">{roleLabel}</div>
            </div>
            <ChevronDown
              className="hidden size-[14px] text-content-3 desktop:block"
              strokeWidth={2}
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-content-3">{user?.email}</p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate("/account")}>Profile</DropdownMenuItem>
          {showFleetAppAction && (
            <>
              <DropdownMenuSeparator />
              {isAdminView ? (
                <DropdownMenuItem onClick={() => navigate("/")}>
                  <ArrowLeft className="size-4" />
                  Exit to customer app
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => enterAdmin()}>
                  <Shield className="size-4" style={{ color: "var(--admin)" }} />
                  Switch to Admin view
                </DropdownMenuItem>
              )}
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout}>Log out</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  )
}
